import { Dispatch, SetStateAction, useMemo, useRef, useState } from 'react';

import { compareObjects } from '@/lib/utils/object';

export type StateField<T> = {
  // Current state value
  value: T;
  // Change the current state
  setValue: Dispatch<SetStateAction<T>>;
  // Returns if the current state is valid after executing the validation function
  isValid: boolean;
  // Tracks if setValue has been called at least once
  isDirty: boolean;
};

export const useFormField = <T,>(
  initialValue: T,
  validate: (value: T) => boolean
): StateField<T> => {
  const isDirtyRef = useRef(false);
  const [value, setValue] = useState(initialValue);
  const initialRef = useRef(value);
  const isValid = validate(value);

  // Compare the initial state with the current state
  if (!compareObjects(initialRef.current, value)) {
    isDirtyRef.current = true;
  }

  return useMemo(
    () => ({ value, setValue, isValid, isDirty: isDirtyRef.current }),
    [value, setValue, isValid, isDirtyRef]
  );
};
